import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Download, Plus, Trash2, Save, FileText, Sparkles, Eye, ArrowLeft, Info } from "lucide-react";

const EMPTY_RESUME = {
  full_name:"", email:"", phone:"", location:"", summary:"",
  experience:[], education:[],
  certifications:[], licenses:[], skills:[], languages:[], portfolio_urls:[],
};

const PAID_SERVICES = [
  { sku:"CS-AI-RESUME-PRO", name:"AI Resume Pro", price:29, sub:"Job-tailored resume" },
  { sku:"CS-COVER-LETTER-BUILDER", name:"AI Cover Letter", price:19, sub:"Job-specific cover letter" },
  { sku:"CS-RESUME-COVER-BUNDLE", name:"Resume + Cover Letter Bundle", price:39, sub:"Best value" },
  { sku:"CS-AI-INTERVIEW-PREP", name:"AI Interview Prep", price:29, sub:"Practice guidance" },
  { sku:"CS-LINKEDIN-OPTIMIZER", name:"LinkedIn Optimizer", price:49, sub:"Profile refresh" },
  { sku:"CS-CAREER-ACCELERATOR", name:"Career Accelerator Bundle", price:99, sub:"Everything included" },
];

export default function CandidateResume() {
  const { user } = useAuth();
  const [tab, setTab] = useState("edit"); // edit | preview | services
  const [resume, setResume] = useState(EMPTY_RESUME);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [msg, setMsg] = useState("");
  const [checkoutMsg, setCheckoutMsg] = useState(null);

  useEffect(() => {
    api.get("/candidate-portal/resume").then(r => {
      const cur = r.data?.resume;
      setResume(cur || { ...EMPTY_RESUME,
        full_name: `${user?.first_name||""} ${user?.last_name||""}`.trim(),
        email: user?.email||"" });
      setSavedAt(r.data?.updated_at || null);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [user]);

  const update = (patch) => setResume(prev => ({...prev, ...patch}));
  const updExp = (i, patch) => setResume(prev => {
    const list = [...prev.experience]; list[i] = {...list[i], ...patch}; return {...prev, experience:list};
  });
  const addExp = () => setResume(prev => ({...prev, experience:[...prev.experience, {employer:"",title:"",start_date:"",end_date:"",responsibilities:[],accomplishments:[]}]}));
  const rmExp = (i) => setResume(prev => ({...prev, experience:prev.experience.filter((_,ix)=>ix!==i)}));
  const updEdu = (i, patch) => setResume(prev => {
    const list = [...prev.education]; list[i] = {...list[i], ...patch}; return {...prev, education:list};
  });
  const addEdu = () => setResume(prev => ({...prev, education:[...prev.education, {institution:"",degree:"",field_of_study:"",start_date:"",end_date:""}]}));
  const rmEdu = (i) => setResume(prev => ({...prev, education:prev.education.filter((_,ix)=>ix!==i)}));

  const save = async () => {
    setMsg(""); setSaving(true);
    try {
      const r = await api.put("/candidate-portal/resume", resume);
      setSavedAt(r.data.updated_at); setMsg("Saved");
    } catch (err) { setMsg(fmtErr(err?.response?.data?.detail) || "Failed to save"); }
    finally { setSaving(false); setTimeout(()=>setMsg(""), 2500); }
  };

  const downloadPdf = async () => {
    if (!savedAt) { setMsg("Save your resume first"); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token");
    try {
      const r = await fetch(`${base}/api/candidate-portal/resume/download.pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(r.status === 404 ? "Save your resume first" : "PDF download failed");
      const blob = await r.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = "resume.pdf"; document.body.appendChild(a); a.click(); a.remove();
    } catch (err) { setMsg(err.message); }
  };

  const startCheckout = async (sku) => {
    setCheckoutMsg(null);
    try {
      const r = await api.post("/candidate-portal/career-services/checkout-url", { product_sku: sku });
      window.open(r.data.checkout_url, "_blank", "noopener");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setCheckoutMsg({ sku, message: d?.message || "Checkout is being configured — our team will reach out." });
    }
  };

  if (!loaded) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <Link to="/candidate-portal" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={12}/> Back to dashboard</Link>
        <div className="flex items-center justify-between mt-2">
          <h1 className="font-display font-bold text-2xl md:text-3xl text-slate-900">My Resume</h1>
          <div className="text-[10px] text-emerald-700 font-semibold uppercase tracking-widest bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1">FREE</div>
        </div>
        <p className="text-sm text-slate-500 mt-1">Basic Resume Builder is free — save, preview, and download the PDF to use in ConnectMyHub applications. Paid Career Services are optional and never affect ranking or matching.</p>

        <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200">
          {[["edit","Edit"],["preview","Preview"],["services","Career Tools"]].map(([k,label]) => (
            <button key={k} onClick={()=>setTab(k)} data-testid={`resume-tab-${k}`}
                    className={`px-4 h-10 text-sm font-semibold ${tab===k?"border-b-2 border-slate-900 text-slate-900":"text-slate-500 hover:text-slate-800"}`}>{label}</button>
          ))}
        </div>

        {tab === "edit" && (
          <div className="mt-6 space-y-6">
            <Card title="Contact information">
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Full name" v={resume.full_name} on={v=>update({full_name:v})} testid="fld-name"/>
                <Field label="Email" v={resume.email} on={v=>update({email:v})} testid="fld-email" type="email"/>
                <Field label="Phone" v={resume.phone} on={v=>update({phone:v})} testid="fld-phone" required={false}/>
                <Field label="Location" v={resume.location} on={v=>update({location:v})} testid="fld-location" required={false}/>
              </div>
            </Card>
            <Card title="Professional summary">
              <Field label="" v={resume.summary} on={v=>update({summary:v})} testid="fld-summary" textarea rows={4} required={false}
                     hint="A short paragraph about your background and what you do best."/>
            </Card>
            <Card title="Employment history"
                   right={<AddBtn onClick={addExp} testid="add-exp"/>}>
              {resume.experience.length === 0 && <Empty label="Add your first role"/>}
              {resume.experience.map((e,i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50 space-y-2" data-testid={`exp-${i}`}>
                  <div className="grid md:grid-cols-2 gap-2">
                    <Field label="Job title" v={e.title} on={v=>updExp(i,{title:v})} testid={`exp-${i}-title`}/>
                    <Field label="Employer" v={e.employer} on={v=>updExp(i,{employer:v})} testid={`exp-${i}-employer`}/>
                    <Field label="Start date" v={e.start_date} on={v=>updExp(i,{start_date:v})} testid={`exp-${i}-start`} required={false} placeholder="Jan 2022"/>
                    <Field label="End date" v={e.end_date} on={v=>updExp(i,{end_date:v})} testid={`exp-${i}-end`} required={false} placeholder="Present"/>
                  </div>
                  <BulletList label="Responsibilities" list={e.responsibilities} on={list=>updExp(i,{responsibilities:list})} testid={`exp-${i}-resp`}/>
                  <BulletList label="Accomplishments" list={e.accomplishments} on={list=>updExp(i,{accomplishments:list})} testid={`exp-${i}-acc`}/>
                  <div className="text-right">
                    <button onClick={()=>rmExp(i)} data-testid={`exp-${i}-del`} className="text-xs text-red-600 inline-flex items-center gap-1"><Trash2 size={12}/> Remove role</button>
                  </div>
                </div>
              ))}
            </Card>
            <Card title="Education" right={<AddBtn onClick={addEdu} testid="add-edu"/>}>
              {resume.education.length === 0 && <Empty label="Add your education"/>}
              {resume.education.map((ed,i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50 space-y-2" data-testid={`edu-${i}`}>
                  <div className="grid md:grid-cols-2 gap-2">
                    <Field label="Institution" v={ed.institution} on={v=>updEdu(i,{institution:v})} testid={`edu-${i}-inst`}/>
                    <Field label="Degree" v={ed.degree} on={v=>updEdu(i,{degree:v})} testid={`edu-${i}-deg`} required={false}/>
                    <Field label="Field of study" v={ed.field_of_study} on={v=>updEdu(i,{field_of_study:v})} testid={`edu-${i}-field`} required={false}/>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Start" v={ed.start_date} on={v=>updEdu(i,{start_date:v})} testid={`edu-${i}-start`} required={false}/>
                      <Field label="End" v={ed.end_date} on={v=>updEdu(i,{end_date:v})} testid={`edu-${i}-end`} required={false}/>
                    </div>
                  </div>
                  <div className="text-right">
                    <button onClick={()=>rmEdu(i)} data-testid={`edu-${i}-del`} className="text-xs text-red-600 inline-flex items-center gap-1"><Trash2 size={12}/> Remove</button>
                  </div>
                </div>
              ))}
            </Card>
            <Card title="Skills, certifications, licenses & languages">
              <ChipInput label="Skills (comma-separated)" list={resume.skills} on={l=>update({skills:l})} testid="fld-skills"/>
              <ChipInput label="Certifications" list={resume.certifications} on={l=>update({certifications:l})} testid="fld-certs"/>
              <ChipInput label="Licenses" list={resume.licenses} on={l=>update({licenses:l})} testid="fld-licenses"/>
              <ChipInput label="Languages" list={resume.languages} on={l=>update({languages:l})} testid="fld-langs"/>
              <ChipInput label="Portfolio / work sample URLs" list={resume.portfolio_urls} on={l=>update({portfolio_urls:l})} testid="fld-portfolio"/>
            </Card>
          </div>
        )}

        {tab === "preview" && <ResumePreview r={resume}/>}

        {tab === "services" && (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {PAID_SERVICES.map(s => (
              <div key={s.sku} data-testid={`svc-${s.sku}`} className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-600">{s.sub}</div>
                <div className="font-display font-bold text-lg text-slate-900 mt-1">{s.name}</div>
                <div className="mt-2 flex items-baseline gap-1"><span className="text-2xl font-display font-bold">${s.price}</span><span className="text-xs text-slate-500">one-time</span></div>
                <p className="text-xs text-slate-500 mt-2">Paid Career Services are optional. They never affect your ranking, qualification, or recruiter consideration.</p>
                <button onClick={()=>startCheckout(s.sku)} data-testid={`svc-buy-${s.sku}`}
                        className="mt-4 w-full h-10 rounded-md bg-slate-900 text-white text-xs font-semibold uppercase tracking-widest hover:bg-slate-800">
                  Purchase
                </button>
                {checkoutMsg?.sku === s.sku && (
                  <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-2 flex items-start gap-1.5" data-testid={`svc-msg-${s.sku}`}>
                    <Info size={12} className="mt-0.5"/> {checkoutMsg.message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Sticky action bar */}
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 shadow z-40">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
            <div className="text-xs text-slate-500 flex-1">
              {savedAt ? `Last saved · ${new Date(savedAt).toLocaleString()}` : "Not saved yet"}
              {msg && <span className="ml-2 text-emerald-700 font-semibold">{msg}</span>}
            </div>
            <button onClick={()=>setTab("preview")} data-testid="btn-preview" className="h-10 px-4 rounded-md border border-slate-300 text-xs font-semibold inline-flex items-center gap-1"><Eye size={12}/> Preview</button>
            <button onClick={downloadPdf} data-testid="btn-download-pdf" className="h-10 px-4 rounded-md border border-slate-300 text-xs font-semibold inline-flex items-center gap-1"><Download size={12}/> PDF</button>
            <button onClick={save} disabled={saving} data-testid="btn-save" className="h-10 px-5 rounded-md bg-slate-900 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"><Save size={12}/> {saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
        <div className="pb-24"/>
      </div>
    </div>
  );
}

function ResumePreview({ r }) {
  return (
    <div className="mt-6 bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-3xl mx-auto" data-testid="resume-preview">
      <div className="font-display text-2xl md:text-3xl font-bold text-slate-900">{r.full_name || "Your Name"}</div>
      <div className="text-xs text-slate-500 mt-1">{[r.email, r.phone, r.location].filter(Boolean).join(" · ")}</div>
      {r.summary && <><Sec label="PROFESSIONAL SUMMARY"/><p className="text-sm text-slate-700 leading-relaxed">{r.summary}</p></>}
      {r.experience?.length > 0 && <><Sec label="EXPERIENCE"/>
        {r.experience.map((e,i) => (
          <div key={i} className="mb-3">
            <div className="text-sm font-semibold text-slate-900">{e.title} <span className="text-slate-500 font-normal">· {e.employer}</span></div>
            <div className="text-[11px] text-slate-500">{[e.start_date, e.end_date || "Present"].filter(Boolean).join(" — ")}</div>
            <ul className="mt-1 space-y-0.5 text-sm text-slate-700 list-disc list-inside">
              {(e.responsibilities||[]).map((b,ix)=><li key={`r${ix}`}>{b}</li>)}
              {(e.accomplishments||[]).map((b,ix)=><li key={`a${ix}`}>★ {b}</li>)}
            </ul>
          </div>
        ))}</>}
      {r.education?.length > 0 && <><Sec label="EDUCATION"/>
        {r.education.map((ed,i)=>(
          <div key={i} className="mb-2">
            <div className="text-sm font-semibold text-slate-900">{[ed.degree, ed.field_of_study].filter(Boolean).join(", ")} — {ed.institution}</div>
            <div className="text-[11px] text-slate-500">{[ed.start_date, ed.end_date].filter(Boolean).join(" — ")}</div>
          </div>
        ))}</>}
      {r.skills?.length > 0 && <><Sec label="SKILLS"/><p className="text-sm text-slate-700">{r.skills.join(", ")}</p></>}
      {r.certifications?.length > 0 && <><Sec label="CERTIFICATIONS"/><ul className="text-sm text-slate-700 list-disc list-inside">{r.certifications.map((c,i)=><li key={i}>{c}</li>)}</ul></>}
      {r.licenses?.length > 0 && <><Sec label="LICENSES"/><ul className="text-sm text-slate-700 list-disc list-inside">{r.licenses.map((c,i)=><li key={i}>{c}</li>)}</ul></>}
      {r.languages?.length > 0 && <><Sec label="LANGUAGES"/><p className="text-sm text-slate-700">{r.languages.join(", ")}</p></>}
      {r.portfolio_urls?.length > 0 && <><Sec label="PORTFOLIO / WORK SAMPLES"/><ul className="text-sm text-teal-700 list-disc list-inside">{r.portfolio_urls.map((u,i)=><li key={i}><a href={u} target="_blank" rel="noreferrer">{u}</a></li>)}</ul></>}
    </div>
  );
}

const Sec = ({label}) => <h3 className="mt-5 mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-800 border-b border-slate-200 pb-0.5">{label}</h3>;

const Card = ({title, right, children}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-sm font-display font-semibold text-slate-900">{title}</h2>
      {right}
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const AddBtn = ({onClick, testid}) => (
  <button onClick={onClick} data-testid={testid} className="text-xs font-semibold text-teal-700 hover:text-teal-800 inline-flex items-center gap-1"><Plus size={12}/> Add</button>
);
const Empty = ({label}) => <div className="text-xs text-slate-500 py-2">{label}</div>;

function Field({label, v, on, testid, type="text", textarea=false, rows=3, required=true, placeholder, hint}) {
  return (
    <div>
      {label && <label className="text-xs font-semibold text-slate-700">{label}</label>}
      {textarea ? (
        <textarea value={v||""} onChange={e=>on(e.target.value)} rows={rows} required={required} placeholder={placeholder}
                  data-testid={testid} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"/>
      ) : (
        <input value={v||""} onChange={e=>on(e.target.value)} type={type} required={required} placeholder={placeholder}
               data-testid={testid} className="mt-1 w-full h-10 rounded-md border border-slate-300 px-3 text-sm"/>
      )}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function ChipInput({label, list, on, testid}) {
  const [txt, setTxt] = useState("");
  const commit = () => {
    const parts = (txt||"").split(",").map(s=>s.trim()).filter(Boolean);
    if (!parts.length) return;
    on([...(list||[]), ...parts]); setTxt("");
  };
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <div className="flex gap-2">
        <input value={txt} onChange={e=>setTxt(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();commit();}}}
               data-testid={testid} placeholder="Type and press Enter"
               className="flex-1 h-10 rounded-md border border-slate-300 px-3 text-sm"/>
        <button type="button" onClick={commit} className="h-10 px-3 rounded-md border border-slate-300 text-xs font-semibold">Add</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(list||[]).map((c,i)=>(
          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-xs">
            {c}
            <button type="button" onClick={()=>on(list.filter((_,ix)=>ix!==i))} className="text-slate-500 hover:text-red-600" aria-label="Remove">×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function BulletList({label, list, on, testid}) {
  const [txt, setTxt] = useState("");
  const add = () => { if (!txt.trim()) return; on([...(list||[]), txt.trim()]); setTxt(""); };
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <div className="flex gap-2">
        <input value={txt} onChange={e=>setTxt(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();add();}}}
               data-testid={testid} placeholder="Add a bullet and press Enter"
               className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-xs"/>
        <button type="button" onClick={add} className="h-9 px-3 rounded-md border border-slate-300 text-xs font-semibold">Add</button>
      </div>
      <ul className="text-xs text-slate-700 space-y-1">
        {(list||[]).map((b,i)=>(
          <li key={i} className="flex items-start gap-1">
            <span>•</span>
            <span className="flex-1">{b}</span>
            <button type="button" onClick={()=>on(list.filter((_,ix)=>ix!==i))} className="text-slate-400 hover:text-red-600" aria-label="Remove"><Trash2 size={11}/></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
